'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState, useCallback, useRef } from 'react';
import { Loader2 } from 'lucide-react';
interface TelegramLoginButtonProps {
  callbackURL?: string;
  onError?: (message: string) => void;
  variant?: 'large' | 'round';
}

/**
 * Telegram Login через deep link на бота.
 *
 * Поток:
 * 1. Генерируем auth-токен → получаем ссылку t.me/BOT?start=auth_TOKEN
 * 2. Пользователь кликает → Telegram открывается → бот шлёт кнопку "Подтвердить"
 * 3. Компонент поллит /api/auth/telegram/status каждые 2 секунды
 * 4. После подтверждения → сессия создаётся → редирект на /
 *
 * variant='large' — полноширинная кнопка-ссылка с текстом и waiting-подсказкой.
 * variant='round' — круглая иконка 48×48 (поллинг работает молча, без текстовой подсказки).
 */
export function TelegramLoginButton({
  callbackURL,
  onError,
  variant = 'large'
}: TelegramLoginButtonProps) {
  const [botUrl, setBotUrl] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [waiting, setWaiting] = useState(false);
  const [loading, setLoading] = useState(true);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Получить токен при маунте
  useEffect(() => {
    let cancelled = false;
    async function fetchToken() {
      try {
        const res = await fetch('/api/auth/telegram/token');
        const data = await res.json();
        if (!cancelled && data.token && data.botUrl) {
          setToken(data.token);
          setBotUrl(data.botUrl);
        }
      } catch {
        onError?.('Не удалось загрузить Telegram-авторизацию');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchToken();
    return () => {
      cancelled = true;
    };
  }, [onError]);

  // Поллинг статуса
  const startPolling = useCallback(() => {
    if (!token || pollRef.current) return;
    setWaiting(true);
    pollRef.current = setInterval(async () => {
      try {
        const res_0 = await fetch(`/api/auth/telegram/status?token=${token}`);
        const data_0 = await res_0.json();
        if (data_0.status === 'confirmed') {
          if (pollRef.current) clearInterval(pollRef.current);
          pollRef.current = null;
          // Завершаем авторизацию — POST создаёт сессию
          const completeRes = await fetch('/api/auth/telegram/complete', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              token
            }),
            credentials: 'same-origin'
          });
          const completeData = await completeRes.json();
          if (completeData.success) {
            window.location.href = callbackURL || '/';
          } else {
            setWaiting(false);
            onError?.(completeData.error || 'Ошибка авторизации');
          }
          return;
        } else if (data_0.status === 'expired') {
          if (pollRef.current) clearInterval(pollRef.current);
          pollRef.current = null;
          setWaiting(false);
          onError?.('Время ожидания истекло. Попробуйте снова.');
          // Перегенерируем токен
          const res2 = await fetch('/api/auth/telegram/token');
          const data2 = await res2.json();
          if (data2.token && data2.botUrl) {
            setToken(data2.token);
            setBotUrl(data2.botUrl);
          }
        } else if (data_0.status === 'error') {
          if (pollRef.current) clearInterval(pollRef.current);
          pollRef.current = null;
          setWaiting(false);
          onError?.(data_0.error || 'Ошибка авторизации');
        }
      } catch {
        // Сетевая ошибка — продолжаем поллить
      }
    }, 800);
  }, [token, onError, callbackURL]);

  // Очистка при размонтировании
  useEffect(() => {
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, []);
  if (loading) {
    if (variant === 'round') {
      return <div className="flex items-center justify-center w-12 h-12">
          <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
        </div>;
    }
    return <div className="flex items-center justify-center h-12">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>;
  }
  if (!botUrl) return null;
  if (variant === 'round') {
    return <a href={botUrl} target="_blank" rel="noopener noreferrer" onClick={() => startPolling()} aria-label="Войти через Telegram" aria-disabled={waiting || undefined} className="flex items-center justify-center w-14 h-14 rounded-full bg-white hover:bg-slate-50 dark:bg-zinc-900 dark:hover:bg-zinc-800 text-slate-800 dark:text-zinc-100 border border-slate-200 dark:border-zinc-700 shadow-[0_1px_2px_rgba(15,23,42,0.04)] hover:shadow-[0_4px_12px_rgba(15,23,42,0.08)] hover:border-slate-300 dark:hover:border-zinc-600 transition-all cursor-pointer">
        {waiting ? <Loader2 className="w-5 h-5 animate-spin" /> : <TelegramIcon className="w-8 h-8" />}
      </a>;
  }
  return <div className="space-y-1.5">
      <a href={botUrl} target="_blank" rel="noopener noreferrer" onClick={() => startPolling()} className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-white hover:bg-gray-50 dark:bg-zinc-900 dark:hover:bg-zinc-800 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-zinc-700 rounded-lg font-medium transition-colors cursor-pointer">
        <TelegramIcon className="w-5 h-5" />
        {waiting ? 'Открываем Telegram...' : 'Войти через Telegram'}
      </a>
      {waiting && <p className="text-xs text-center text-muted-foreground animate-pulse">
          Откройте бота — вход завершится автоматически
        </p>}
    </div>;
}
function TelegramIcon(t0) {
  const $ = _c(3);
  const {
    className
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <path fill="#2AABEE" d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.479.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== className) {
    t2 = <svg className={className} viewBox="0 0 24 24" fill="none">{t1}</svg>;
    $[1] = className;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}